import {type AddressType, type ApplePayResponseType} from 'constants/flow/flowTypesCheckout';
import {
    type PaymentType,
    type PaymentOsType,
    type PaymentDataMapType,
} from 'constants/flow/flowTypes';

/**
 * Converts addresses info from apple structure to ours
 * @param {ApplePayResponseType} responseApple response from Apple
 * @returns {{shippingAddress: AddressType, billingAddress: AddressType}} addresses data to save in store
 */
export const getConvertedAddressesObject = (
    responseApple: ApplePayResponseType,
): {shippingAddress: AddressType, billingAddress: AddressType} => {
    const {billingAddress} = responseApple.details;
    const payerNames = responseApple.payerName.split(' ');

    const {shippingAddress} = responseApple;
    const contactNames = shippingAddress.recipient.split(' ');
    return {
        shippingAddress: {
            firstName: contactNames[0],
            lastName: contactNames[1],
            email: responseApple.payerEmail,
            street: shippingAddress.addressLine.join(', '),
            city: shippingAddress.city,
            region: shippingAddress.region,
            zipCode: shippingAddress.postalCode,
            country: shippingAddress.country,
            phone: shippingAddress.phone,
        },
        billingAddress: {
            firstName: payerNames[0],
            lastName: payerNames[1],
            email: responseApple.payerEmail,
            street: billingAddress.addressLine.join(', '),
            city: billingAddress.city,
            region: billingAddress.region,
            zipCode: billingAddress.postalCode,
            country: billingAddress.country,
            phone: responseApple.payerPhone,
        },
    };
};

/**
 * Defines if apple pay is available
 * @returns {boolean} true if apple pay is available
 */
export const isApplePayAvailable = (): boolean =>
    Boolean(window.ApplePaySession && ApplePaySession.canMakePayments());

/**
 * Convert data from active payment from store to structure appropriate for backend
 * @param {PaymentOsType | PaymentType} paymentInfo - active payment from store
 * @returns {PaymentDataMapType} true if apple pay is available
 */
export const getConvertedPaymentObject = (
    paymentInfo: PaymentOsType | PaymentType,
): PaymentDataMapType => ({
    method: paymentInfo.code,
    save_card: paymentInfo.saveCard ? 1 : 0,
    token: paymentInfo.paymentMethodToken,
    session_id: paymentInfo.sessionId,
    is_iframe: 1,
    cc_payment_token: paymentInfo.paymentToken || '',
    cc_saved_card: paymentInfo.paymentMethodToken,
    cc_customer_email: paymentInfo.customerEmail || '',
    cc_customer_name: paymentInfo.customerName || '',
    cc_saved_cid: paymentInfo.saveCid || '',
    cc_type: paymentInfo.type2 || '', // TODO: Mary check if it works in some cases
    cc_number: (paymentInfo.cardNumber || '').replace(/-/g, ''),
    cc_exp_month: paymentInfo.month,
    cc_exp_year: paymentInfo.year,
    cc_cid: paymentInfo.cvv,
    payment_method_id: paymentInfo.paymentId,
});
